import axios, { AxiosInstance } from "axios";
import {
  ApiResponse,
  PaginatedResponse,
  AuthTokens,
  User,
  Marketer,
  Order,
  MarketerDashboard,
  AnalyticsData,
  StatusStats,
  PerformanceMetrics,
  Notification,
  OrderStatusUpdateForm,
  MarketerProfileForm,
  MenuItemSnapshot,
  MenuItemSnapshotForm,
  MenuItemsByMarket,
  Market,
} from "../types";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://192.168.1.131:8000/api/v1";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const tokens = this.getStoredTokens();
        if (tokens?.access) {
          config.headers.Authorization = `Bearer ${tokens.access}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const tokens = this.getStoredTokens();
            if (tokens?.refresh) {
              const newTokens = await this.refreshToken(tokens.refresh);
              this.storeTokens(newTokens);

              // Retry the original request with new token
              originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.clearTokens();
            window.location.href = "/login";
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management
  private getStoredTokens(): AuthTokens | null {
    const tokens = localStorage.getItem("tokens");
    return tokens ? JSON.parse(tokens) : null;
  }

  private storeTokens(tokens: AuthTokens): void {
    localStorage.setItem("tokens", JSON.stringify(tokens));
  }

  private clearTokens(): void {
    localStorage.removeItem("tokens");
  }

  // Authentication - PIN-based for marketers
  async loginWithPin(
    phoneNumber: string,
    pin: string
  ): Promise<{
    access: string;
    refresh: string;
    marketer: Marketer;
    session_token: string;
  }> {
    const response = await this.api.post("/marketer/auth/login/", {
      phone_number: phoneNumber,
      pin: pin,
    });

    const tokens = {
      access: response.data.data.access,
      refresh: response.data.data.refresh,
    };
    this.storeTokens(tokens);

    // Store session token separately
    localStorage.setItem("session_token", response.data.data.session_token);

    // Transform marketer data
    const marketer = this.transformMarketerData(response.data.data.marketer);

    return {
      ...response.data.data,
      marketer,
    };
  }

  // Legacy OTP login (kept for backward compatibility)
  async login(phoneNumber: string, otp: string): Promise<AuthTokens> {
    const response = await this.api.post<AuthTokens>("/user/login/", {
      phone_number: phoneNumber,
      otp: otp,
    });
    this.storeTokens(response.data);
    return response.data;
  }

  async requestOTP(phoneNumber: string): Promise<void> {
    await this.api.post("/user/initiate-login/", {
      phone_number: phoneNumber,
    });
  }

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const response = await this.api.post<AuthTokens>("/token/refresh/", {
      refresh: refreshToken,
    });
    return response.data;
  }

  async logout(): Promise<void> {
    const sessionToken = localStorage.getItem("session_token");
    if (sessionToken) {
      try {
        await this.api.post("/marketer/auth/logout/", {
          session_token: sessionToken,
        });
      } catch (error) {
        console.error("Logout API call failed:", error);
      }
    }

    this.clearTokens();
    localStorage.removeItem("session_token");
  }

  // User profile
  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<User>("/user/profile/");
    return response.data;
  }

  // Helper to transform camelCase to snake_case for marketer data
  private transformMarketerData(data: any): Marketer {
    return {
      id: data.id,
      user: data.user,
      business_name: data.businessName || data.business_name,
      phone_number: data.phoneNumber || data.phone_number,
      profile_image: data.profileImage || data.profile_image,
      is_active: data.isActive ?? data.is_active ?? true,
      is_available: data.isAvailable ?? data.is_available ?? false,
      rating: data.rating || 0,
      total_orders: data.totalOrders ?? data.total_orders ?? 0,
      completed_orders: data.completedOrders ?? data.completed_orders ?? 0,
      completion_rate: data.completionRate ?? data.completion_rate ?? 0,
      current_active_orders: data.currentActiveOrders ?? data.current_active_orders ?? 0,
      max_concurrent_orders: data.maxConcurrentOrders ?? data.max_concurrent_orders ?? 5,
      available_cities: data.availableCities || data.available_cities || [],
      created_at: data.createdAt || data.created_at,
      updated_at: data.updatedAt || data.updated_at,
    };
  }

  // Marketer endpoints
  async getMarketerProfile(): Promise<Marketer> {
    const response = await this.api.get<ApiResponse<any>>(
      "/marketer/auth/profile/"
    );
    // The API returns nested data structure
    const marketerData = response.data.data?.marketer || response.data.data;
    return this.transformMarketerData(marketerData);
  }

  async createMarketerProfile(data: MarketerProfileForm): Promise<Marketer> {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === "available_cities" && Array.isArray(value)) {
        value.forEach((cityId) =>
          formData.append("available_cities", cityId.toString())
        );
      } else if (key === "profile_image" && value instanceof File) {
        formData.append(key, value);
      } else if (value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    const response = await this.api.post<ApiResponse<any>>(
      "/marketer/profile/profile/",
      formData,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return this.transformMarketerData(response.data.data);
  }

  async updateMarketerProfile(
    data: Partial<MarketerProfileForm>
  ): Promise<Marketer> {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === "available_cities" && Array.isArray(value)) {
        value.forEach((cityId) =>
          formData.append("available_cities", cityId.toString())
        );
      } else if (key === "profile_image" && value instanceof File) {
        formData.append(key, value);
      } else if (value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    const response = await this.api.post<ApiResponse<any>>(
      "/marketer/profile/profile/",
      formData,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return this.transformMarketerData(response.data.data);
  }

  async getMarketerDashboard(): Promise<MarketerDashboard> {
    const response = await this.api.get<ApiResponse<MarketerDashboard>>(
      "/marketer/profile/dashboard/"
    );
    return response.data.data;
  }

  // Analytics
  async getMarketerAnalytics(days: number = 30): Promise<AnalyticsData> {
    const response = await this.api.get<ApiResponse<AnalyticsData>>(
      `/marketer/profile/analytics/?days=${days}`
    );
    return response.data.data;
  }

  // Orders
  async getMyOrders(params?: {
    status?: string;
    page?: number;
  }): Promise<PaginatedResponse<Order>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<Order>>>(
      "/marketer/orders/",
      { params }
    );
    return response.data.data;
  }

  async getAvailableOrders(): Promise<Order[]> {
    const response = await this.api.get<ApiResponse<Order[]>>(
      "/marketer/orders/available/"
    );
    return response.data.data;
  }

  async getMarketerOrders(): Promise<PaginatedResponse<Order>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<Order>>>(
      "/marketer/orders/"
    );
    return response.data.data;
  }

  async getOrderById(orderId: number): Promise<Order> {
    const response = await this.api.get<ApiResponse<Order>>(
      `/marketer/orders/${orderId}/`
    );
    return response.data.data;
  }

  // Market Lists with advanced filtering
  async getMarketLists(params?: {
    status?: string;
    market?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Order>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<Order>>>(
      "/marketer/orders/market_lists/",
      { params }
    );
    return response.data.data;
  }

  // Order Status Statistics
  async getOrderStatusStats(): Promise<StatusStats> {
    const response = await this.api.get<ApiResponse<StatusStats>>(
      "/marketer/orders/status_stats/"
    );
    return response.data.data;
  }

  async updateOrderStatus(
    orderId: number,
    data: OrderStatusUpdateForm
  ): Promise<Order> {
    const response = await this.api.post<ApiResponse<Order>>(
      `/marketer/orders/${orderId}/update_status/`,
      data
    );
    return response.data.data;
  }

  // Analytics
  async getPerformanceMetrics(params?: {
    period?: "day" | "week" | "month";
    start_date?: string;
    end_date?: string;
  }): Promise<PerformanceMetrics[]> {
    const response = await this.api.get<ApiResponse<PerformanceMetrics[]>>(
      "/marketer/analytics/",
      { params }
    );
    return response.data.data;
  }

  // Notifications
  async getNotifications(params?: {
    page?: number;
  }): Promise<PaginatedResponse<Notification>> {
    const response = await this.api.get<
      ApiResponse<PaginatedResponse<Notification>>
    >("/notification/notifications/", { params });
    return response.data.data;
  }

  async getUnreadNotifications(): Promise<Notification[]> {
    const response = await this.api.get<ApiResponse<Notification[]>>(
      "/notification/notifications/unread/"
    );
    return response.data.data;
  }

  async markNotificationAsRead(notificationId: number): Promise<void> {
    await this.api.post(
      `/notification/notifications/${notificationId}/mark_read/`
    );
  }

  async markMultipleNotificationsAsRead(
    notificationIds: number[]
  ): Promise<void> {
    await this.api.post("/notification/notifications/mark_read_bulk/", {
      notification_ids: notificationIds,
    });
  }

  async getNotificationStats(): Promise<{
    total_notifications: number;
    unread_count: number;
    read_count: number;
    by_type: Record<string, number>;
    by_priority: Record<string, number>;
  }> {
    const response = await this.api.get<ApiResponse<any>>(
      "/notification/notifications/stats/"
    );
    return response.data.data;
  }

  // Polling endpoint (replaces WebSocket)
  async pollForUpdates(lastPoll?: string): Promise<any> {
    const params: any = {};
    if (lastPoll) {
      params.last_poll = lastPoll;
    }
    const response = await this.api.get("/marketer/poll/", { params });
    return response.data;
  }

  // Menu Item Snapshots
  async getMenuSnapshots(params?: {
    market?: number;
    page?: number;
  }): Promise<PaginatedResponse<MenuItemSnapshot>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<MenuItemSnapshot>>>(
      "/marketer/menu-snapshots/",
      { params }
    );
    return response.data.data;
  }

  async getMenuSnapshotById(id: number): Promise<MenuItemSnapshot> {
    const response = await this.api.get<ApiResponse<MenuItemSnapshot>>(
      `/marketer/menu-snapshots/${id}/`
    );
    return response.data.data;
  }

  async createMenuSnapshot(data: MenuItemSnapshotForm): Promise<MenuItemSnapshot> {
    const formData = new FormData();
    formData.append("market", data.market.toString());
    formData.append("name", data.name);
    formData.append("image", data.image);
    formData.append("cost", data.cost.toString());
    if (data.currency) {
      formData.append("currency", data.currency);
    }
    if (data.description) {
      formData.append("description", data.description);
    }

    const response = await this.api.post<ApiResponse<MenuItemSnapshot>>(
      "/marketer/menu-snapshots/",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data.data;
  }

  async updateMenuSnapshot(
    id: number,
    data: Partial<MenuItemSnapshotForm>
  ): Promise<MenuItemSnapshot> {
    const formData = new FormData();
    if (data.market !== undefined) {
      formData.append("market", data.market.toString());
    }
    if (data.name) {
      formData.append("name", data.name);
    }
    if (data.image) {
      formData.append("image", data.image);
    }
    if (data.cost !== undefined) {
      formData.append("cost", data.cost.toString());
    }
    if (data.currency) {
      formData.append("currency", data.currency);
    }
    if (data.description !== undefined) {
      formData.append("description", data.description);
    }

    const response = await this.api.patch<ApiResponse<MenuItemSnapshot>>(
      `/marketer/menu-snapshots/${id}/`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data.data;
  }

  async deleteMenuSnapshot(id: number): Promise<void> {
    await this.api.delete(`/marketer/menu-snapshots/${id}/`);
  }

  async getMenuSnapshotsByMarket(): Promise<MenuItemsByMarket> {
    const response = await this.api.get<ApiResponse<MenuItemsByMarket>>(
      "/marketer/menu-snapshots/by_market/"
    );
    return response.data.data;
  }

  async getRecentMenuSnapshots(limit: number = 10): Promise<MenuItemSnapshot[]> {
    const response = await this.api.get<ApiResponse<MenuItemSnapshot[]>>(
      `/marketer/menu-snapshots/recent/?limit=${limit}`
    );
    return response.data.data;
  }

  // Markets - for dropdown selection
  async getMarkets(): Promise<Market[]> {
    const response = await this.api.get<ApiResponse<Market[]>>("/core/markets/");
    return response.data.data;
  }

  // Utility methods
  isAuthenticated(): boolean {
    const tokens = this.getStoredTokens();
    return !!tokens?.access;
  }

  getAuthTokens(): AuthTokens | null {
    return this.getStoredTokens();
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();
export default apiService;
